import CustomButton from 'components/Buttons/CustomButton'
import { monthsOptions } from 'constants'
import { useLazyGetArusKasQuery } from 'features/private-views/cash-flow/cashFlowApiSlice'
import ProfitLossCardSection from 'features/private-views/cash-flow/tabs/ProfitLossCardSection'
import moment from 'moment-timezone'
import { useEffect, useState } from 'react'
import Chart from 'react-apexcharts'
import { ChevronRight, TrendingUp } from 'react-feather'
import { useNavigate } from 'react-router-dom'
import { Card, CardBody, Spinner } from 'reactstrap'
import styled from 'styled-components'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { URL_ARUS_KAS } from 'utils/pathUrl'
import { COLOR_MAP } from 'config/templateConfig'

const ScrollableContainer = styled.div`
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  padding: 10px 0;
  -webkit-overflow-scrolling: touch;

  /* Sembunyikan scrollbar untuk Chrome, Safari, dan Opera */
  &::-webkit-scrollbar {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }

  /* Sembunyikan scrollbar untuk IE, Edge, dan Firefox */
  -ms-overflow-style: none !important;
  scrollbar-width: none !important;
`

const ChartContainer = styled.div`
  width: 100%;
  min-width: ${(props) => (props.isMobile ? '700px' : '100%')};
`

const SectionCashFlowWidget = ({ params = {} }) => {
  const navigate = useNavigate()
  const [getArusKas, res] = useLazyGetArusKasQuery()

  const [dataIncome, setDataIncome] = useState(Array(12).fill(0))
  const [dataExpense, setDataExpense] = useState(Array(12).fill(0))
  const [totalIncome, setTotalIncome] = useState(0)
  const [totalExpense, setTotalExpense] = useState(0)
  const [totalProfit, setTotalProfit] = useState(0)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  const currentYear = new Date().getFullYear()

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const fetchCashFlowData = async () => {
      try {
        const resultResponse = await getArusKas({
          startdate: `${currentYear}-01-01`,
          enddate: `${currentYear}-12-31`,
          filter: 'all',
          page: 0,
          size: 10000,
        }).unwrap()

        const rawData = resultResponse?.items || []

        const { totalIncome, totalExpense, incomeByMonth, expenseByMonth } =
          rawData.reduce(
            (acc, item) => {
              const monthIndex = moment(item?.createddate)?.month()

              if (item?.incomeorexpense === 'income') {
                acc.totalIncome += item?.amount
                acc.incomeByMonth[monthIndex] += item?.amount
              } else if (item?.incomeorexpense === 'expense') {
                acc.totalExpense += item?.amount
                acc.expenseByMonth[monthIndex] += item?.amount
              }
              return acc
            },
            {
              totalIncome: 0,
              totalExpense: 0,
              incomeByMonth: Array(12).fill(0),
              expenseByMonth: Array(12).fill(0),
            },
          )

        setTotalIncome(totalIncome)
        setTotalExpense(totalExpense)
        setTotalProfit(totalIncome - totalExpense)
        setDataIncome(incomeByMonth)
        setDataExpense(expenseByMonth)
      } catch (error) {
        console.warn('Error fetching cash flow data for widget', error)
      }
    }

    fetchCashFlowData()
  }, [getArusKas, currentYear])

  const optionsAnnual = {
    chart: {
      type: 'bar',
      height: isMobile ? 350 : 400,
      zoom: { enabled: false },
      toolbar: { show: false },
      stacked: false,
      parentHeightOffset: 0,
    },
    plotOptions: {
      bar: {
        borderRadius: isMobile ? 1 : 5,
        horizontal: false,
        columnWidth: '50%',
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'all',
        distributed: false,
      },
    },
    legend: {
      show: true,
      showForSingleSeries: true,
      position: 'bottom',
      horizontalAlign: 'center',
      markers: { shape: 'circle' },
    },
    colors: ['#039855', '#D92D20'],
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2 },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      y: {
        formatter(value) {
          return newFormatThousandSeparator(value)
        },
      },
    },
    xaxis: {
      categories: monthsOptions.map((month) => month?.label?.slice(0, 3)),
      labels: {
        style: { fontSize: isMobile ? '10px' : '12px', colors: '#6E6E6E' },
        rotate: isMobile ? -90 : -45,
        offsetY: isMobile ? 0 : 5,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    title: {
      text: 'Jumlah (Rupiah)',
      offsetY: isMobile ? 0 : 5,
      style: { fontSize: '12px', fontWeight: 'bold' },
    },
    yaxis: {
      labels: {
        formatter(value) {
          return newFormatThousandSeparator(value)
        },
        style: { fontSize: isMobile ? '10px' : '12px', colors: '#6E6E6E' },
        offsetX: isMobile ? -10 : 0,
        rotate: isMobile ? -60 : -0,
        align: 'right',
      },
      tickAmount: isMobile ? 4 : 5,
      min: 0,
      max: Math.max(...dataIncome, ...dataExpense) * 1.2,
      forceNiceScale: true,
      floating: false,
      decimalsInFloat: 0,
      axisBorder: { show: true },
      axisTicks: { show: true },
    },
    grid: {
      borderColor: '#f1f1f1',
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, right: 0, bottom: 0, left: isMobile ? 5 : 10 },
    },
  }

  const seriesAnnual = [
    { name: 'Pemasukan', data: dataIncome },
    { name: 'Pengeluaran', data: dataExpense },
  ]

  return (
    <Card
      className="border-0 shadow-sm mt-4"
      style={{
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        overflow: 'hidden',
      }}
      onClick={() => navigate(URL_ARUS_KAS)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-5px)'
        e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
      }}
    >
      <CardBody className="p-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <div
              className="me-3 d-flex align-items-center justify-content-center rounded-circle"
              style={{
                width: '40px',
                height: '40px',
                backgroundColor: COLOR_MAP[params.color] ? `${COLOR_MAP[params.color]}15` : 'rgba(0, 0, 0, 0.05)',
                color: COLOR_MAP[params.color] || '#1e293b',
                border: `1px solid ${COLOR_MAP[params.color] ? `${COLOR_MAP[params.color]}30` : 'rgba(0, 0, 0, 0.1)'}`,
                backdropFilter: 'blur(8px)',
              }}
            >
              <TrendingUp size={20} />
            </div>
            <div>
              <h5 className="mb-1 fw-bold text-dark">Laporan Arus Kas</h5>
              <p className="mb-0 text-muted" style={{ fontSize: 15 }}>
                Ringkasan performa keuangan Anda di tahun {currentYear}
              </p>
            </div>
          </div>
          <CustomButton
            color="link"
            className="text-decoration-none fw-semibold p-0 d-flex align-items-center gap-1"
            onClick={() => navigate(URL_ARUS_KAS)}
          >
            <div
              className="d-flex align-items-center justify-content-center rounded-circle"
              style={{
                width: 32,
                height: 32,
                background: COLOR_MAP[params.color] ? `${COLOR_MAP[params.color]}15` : '#E7F1FF',
              }}
            >
              <ChevronRight size={18} color={COLOR_MAP[params.color] || "#0D6EFD"} />
            </div>
          </CustomButton>
        </div>

        {res.isFetching && !res.data ? (
          <div className="d-flex justify-content-center align-items-center p-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            <ProfitLossCardSection
              res={res}
              totalExpense={totalExpense}
              totalIncome={totalIncome}
              totalProfit={totalProfit}
            />

            <div className="mt-4 pt-4 border-top">
              <h6 className="fw-bold mb-3 text-dark">
                Grafik Arus Kas {currentYear}
              </h6>
              <ScrollableContainer isMobile={isMobile}>
                <ChartContainer isMobile={isMobile}>
                  <Chart
                    options={optionsAnnual}
                    series={seriesAnnual}
                    type="bar"
                    height={isMobile ? 350 : 400}
                  />
                </ChartContainer>
              </ScrollableContainer>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  )
}

export default SectionCashFlowWidget
